import { useNavigate } from 'react-router-dom';
import { Modal } from '../components/Modal';
import { TypeAhead, type TypeAheadItem } from '../components/TypeAhead';
import { useUiStore } from '../stores/ui';
import { usePermission } from '../lib/permissions';
import { useCompanyStore } from '../stores/company';

interface Command extends TypeAheadItem {
  run: () => void;
}

/** Ctrl+K / `/` — global command palette (PRD §12.1). */
export function CommandPalette() {
  const open = useUiStore((s) => s.commandPaletteOpen);
  const setOpen = useUiStore((s) => s.setCommandPaletteOpen);
  const setCheatSheetOpen = useUiStore((s) => s.setCheatSheetOpen);
  const setCompanySwitcherOpen = useUiStore((s) => s.setCompanySwitcherOpen);
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const navigate = useNavigate();
  const can = usePermission();
  const activeCompanyId = useCompanyStore((s) => s.activeCompanyId);

  const commands: Command[] = [
    {
      key: 'nav-money',
      label: 'Open Money desk',
      sublabel: 'Receipts, payments, notes & controls',
      run: () => navigate('/money'),
    },
    ...(can('voucher:payment:create')
      ? [{ key: 'new-payment', label: 'New Payment voucher', sublabel: 'F5 · instrument + bill allocation', run: () => navigate('/money/payment/new') }]
      : []),
    ...(can('voucher:receipt:create')
      ? [{ key: 'new-receipt', label: 'New Receipt voucher', sublabel: 'F6 · instrument + bill allocation', run: () => navigate('/money/receipt/new') }]
      : []),
    ...(can('voucher:debit_note:create')
      ? [{ key: 'new-debit-note', label: 'New Debit Note', sublabel: 'Rate difference / interest', run: () => navigate('/money/debit-note/new') }]
      : []),
    ...(can('voucher:debit_note:create')
      ? [{ key: 'new-physical-debit-note', label: 'New physical purchase return', sublabel: 'Debit Note · source inventory + Against Ref', run: () => navigate('/goods/debit-note/new') }]
      : []),
    ...(can('voucher:credit_note:create')
      ? [{ key: 'new-credit-note', label: 'New Credit Note', sublabel: 'Sales return / allowance', run: () => navigate('/money/credit-note/new') }]
      : []),
    ...(can('voucher:credit_note:create')
      ? [{ key: 'new-physical-credit-note', label: 'New physical sales return', sublabel: 'Credit Note · source inventory + Against Ref', run: () => navigate('/goods/credit-note/new') }]
      : []),
    ...(can('report:outstandings:view')
      ? [{ key: 'nav-outstandings', label: 'Open Outstandings & ageing', sublabel: 'Party → bill → follow-up', run: () => navigate('/reports/outstandings') }]
      : []),
    ...(can('bank_reconciliation:statement:view')
      ? [{ key: 'nav-bank-reconciliation', label: 'Open Bank reconciliation', sublabel: 'Import, match & BRS', run: () => navigate('/reports/bank-reconciliation') }]
      : []),
    ...(can('company:company:create')
      ? [
          {
            key: 'nav-new-company',
            label: 'Create a new company',
            sublabel: 'Greenfield books setup',
            run: () => navigate('/companies/new'),
          },
        ]
      : []),
    ...(activeCompanyId
      ? [
          {
            key: 'nav-onboarding',
            label: 'Open Getting started',
            sublabel: 'Company setup progress',
            run: () => navigate('/onboarding'),
          },
        ]
      : []),
    {
      key: 'nav-coa',
      label: 'Go to Chart of accounts',
      run: () => navigate('/coa'),
    },
    ...(can('master:item:view')
      ? [
          {
            key: 'nav-items',
            label: 'Go to Items & GST rates',
            sublabel: 'Alt+I · resolved-rate history',
            run: () => navigate('/masters/items'),
          },
        ]
      : []),
    ...(can('voucher:sales:create')
      ? [
          {
            key: 'new-sales',
            label: 'New Sales invoice',
            sublabel: 'F8 · goods, GST & receivable',
            run: () => navigate('/goods/sales/new'),
          },
        ]
      : []),
    ...(can('voucher:purchase:create')
      ? [
          {
            key: 'new-purchase',
            label: 'New Purchase voucher',
            sublabel: 'F9 · goods, GST & payable',
            run: () => navigate('/goods/purchase/new'),
          },
        ]
      : []),
    ...(can('report:sales_register:view')
      ? [
          {
            key: 'sales-register',
            label: 'Open Sales register',
            sublabel: 'Goods invoice summary and export',
            run: () => navigate('/goods/sales/register'),
          },
        ]
      : []),
    ...(can('report:purchase_register:view')
      ? [
          {
            key: 'purchase-register',
            label: 'Open Purchase register',
            sublabel: 'Goods purchase summary and export',
            run: () => navigate('/goods/purchase/register'),
          },
        ]
      : []),
    ...(can('master:item_group:view')
      ? [
          {
            key: 'nav-catalog',
            label: 'Open item groups & units',
            sublabel: 'Catalog foundations',
            run: () => navigate('/masters/catalog'),
          },
        ]
      : []),
    ...(can('master:party_address:view')
      ? [
          {
            key: 'nav-party-addresses',
            label: 'Open party bill-to / ship-to addresses',
            run: () => navigate('/masters/party-addresses'),
          },
        ]
      : []),
    ...(can('master:voucher_class:view')
      ? [
          {
            key: 'nav-voucher-classes',
            label: 'Open voucher classes',
            run: () => navigate('/settings/voucher-classes'),
          },
        ]
      : []),
    ...(can('master:invoice_series:view')
      ? [
          {
            key: 'nav-invoice-series',
            label: 'Open invoice series',
            run: () => navigate('/settings/invoice-series'),
          },
        ]
      : []),
    ...(can('master:goods_posting_account_mapping:view')
      ? [
          {
            key: 'nav-posting-accounts',
            label: 'Open goods posting accounts',
            run: () => navigate('/settings/posting-accounts'),
          },
        ]
      : []),
    ...(can('master:print_template:view')
      ? [
          {
            key: 'nav-print-templates',
            label: 'Open print templates',
            run: () => navigate('/settings/print-templates'),
          },
        ]
      : []),
    ...(can('voucher:journal:view') || can('voucher:contra:view')
      ? [
          {
            key: 'nav-vouchers',
            label: 'Go to Journal / Contra vouchers',
            sublabel: 'F4 / F7',
            run: () => navigate('/vouchers'),
          },
        ]
      : []),
    ...([
      'report:balance_sheet:view',
      'report:trial_balance:view',
      'report:day_book:view',
      'report:ledger:view',
      'report:group_summary:view',
    ].some(can)
      ? [
          {
            key: 'nav-reports',
            label: 'Open Reports desk',
            sublabel: 'Financial statements & drill-down',
            run: () => navigate('/reports'),
          },
        ]
      : []),
    ...(can('report:balance_sheet:view')
      ? [
          {
            key: 'nav-balance-sheet',
            label: 'Open Balance Sheet',
            run: () => navigate('/reports/balance-sheet'),
          },
        ]
      : []),
    ...(can('report:trial_balance:view')
      ? [
          {
            key: 'nav-trial-balance',
            label: 'Open Trial Balance',
            run: () => navigate('/reports/trial-balance'),
          },
        ]
      : []),
    ...(can('report:day_book:view')
      ? [
          {
            key: 'nav-day-book',
            label: 'Open Day Book',
            run: () => navigate('/reports/day-book'),
          },
        ]
      : []),
    ...(can('report:ledger:view')
      ? [
          {
            key: 'nav-ledger-report',
            label: 'Open Ledger report',
            run: () => navigate('/reports/ledger'),
          },
        ]
      : []),
    ...(can('report:group_summary:view')
      ? [
          {
            key: 'nav-group-summary',
            label: 'Open Group Summary',
            run: () => navigate('/reports/group-summary'),
          },
        ]
      : []),
    {
      key: 'nav-features',
      label: 'Go to Company features (F11)',
      run: () => navigate('/company/features'),
    },
    {
      key: 'nav-branches',
      label: 'Go to Branches',
      run: () => navigate('/company/branches'),
    },
    {
      key: 'nav-fy',
      label: 'Go to Financial years',
      run: () => navigate('/company/financial-years'),
    },
    {
      key: 'nav-locks',
      label: 'Go to Period locks',
      run: () => navigate('/company/period-locks'),
    },
    { key: 'nav-users', label: 'Go to Users', run: () => navigate('/users') },
    { key: 'nav-roles', label: 'Go to Roles', run: () => navigate('/roles') },
    {
      key: 'nav-audit',
      label: 'Go to Edit log / activity',
      run: () => navigate('/audit'),
    },
    {
      key: 'nav-health',
      label: 'Go to System health',
      run: () => navigate('/system/health'),
    },
    {
      key: 'cmd-switch-company',
      label: 'Switch company…',
      sublabel: 'Alt+F3',
      run: () => setCompanySwitcherOpen(true),
    },
    {
      key: 'cmd-cheatsheet',
      label: 'Show keyboard shortcuts…',
      sublabel: 'Alt+H',
      run: () => setCheatSheetOpen(true),
    },
    {
      key: 'cmd-theme',
      label: 'Toggle light / dark theme',
      sublabel: 'Alt+T',
      run: () => toggleTheme(),
    },
  ];

  function handleSelect(cmd: TypeAheadItem) {
    const command = commands.find((c) => c.key === cmd.key);
    setOpen(false);
    command?.run();
  }

  return (
    <Modal open={open} onClose={() => setOpen(false)} title="Command palette" widthClassName="max-w-md">
      <TypeAhead items={commands} onSelect={handleSelect} placeholder="Type a command or screen name…" autoFocus />
    </Modal>
  );
}
